import React, { useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader } from './ui/card';
import { FRAIS_GENERALES_CATEGORIES, newFraisGenerauxLine, parseNum } from './devisQuoteState';

const fmtEur = (n) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
    Number.isFinite(n) ? n : 0
  );

const lineAmount = (line) => parseNum(line.amountHt, 0);

const DevisQuoteFraisGenerauxTab = ({ fraisGeneraux, theme, patchFraisGeneraux, canEdit }) => {
  const inputStyle = {
    borderColor: theme.border,
    backgroundColor: theme.inputBackground,
    color: theme.text,
  };

  const linesByCategory = fraisGeneraux?.linesByCategory || {};

  const categoryTotals = useMemo(() => {
    const out = {};
    for (const cat of FRAIS_GENERALES_CATEGORIES) {
      const lines = linesByCategory[cat.id] || [];
      out[cat.id] = lines.reduce((s, l) => s + lineAmount(l), 0);
    }
    return out;
  }, [linesByCategory]);

  const totalAnnuel = useMemo(
    () => Object.values(categoryTotals).reduce((a, b) => a + b, 0),
    [categoryTotals]
  );

  const addLine = (catId) => {
    patchFraisGeneraux((prev) => ({
      ...prev,
      linesByCategory: {
        ...prev.linesByCategory,
        [catId]: [...(prev.linesByCategory[catId] || []), newFraisGenerauxLine()],
      },
    }));
  };

  const removeLine = (catId, lineId) => {
    patchFraisGeneraux((prev) => {
      const cur = prev.linesByCategory[catId] || [];
      return {
        ...prev,
        linesByCategory: {
          ...prev.linesByCategory,
          [catId]: cur.filter((l) => l.id !== lineId),
        },
      };
    });
  };

  const updateLine = (catId, lineId, patch) => {
    patchFraisGeneraux((prev) => {
      const cur = prev.linesByCategory[catId] || [];
      return {
        ...prev,
        linesByCategory: {
          ...prev.linesByCategory,
          [catId]: cur.map((l) => (l.id === lineId ? { ...l, ...patch } : l)),
        },
      };
    });
  };

  const repartitionOk = (line) => {
    const c = parseNum(line.clientPct, 0);
    const p = parseNum(line.prestatairePct, 0);
    return Math.abs(c + p - 100) < 0.02;
  };

  const fieldH = 'h-10 min-h-[40px] w-full min-w-0 rounded-xl border-2 px-3 text-base tabular-nums sm:h-9 sm:text-sm touch-manipulation';

  return (
    <div className="space-y-5 min-w-0 sm:space-y-6">
      <div
        className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 px-4 py-4 shadow-sm"
        style={{ borderColor: theme.accent, backgroundColor: theme.accentSoft }}
      >
        <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
          Total annuel des frais généraux (HT)
        </p>
        <p className="text-lg font-bold tabular-nums sm:text-xl shrink-0" style={{ color: theme.text }}>
          {fmtEur(totalAnnuel)} HT
        </p>
      </div>

      <p
        className="text-sm leading-relaxed rounded-xl border px-4 py-3"
        style={{ borderColor: theme.border, backgroundColor: theme.panelInset, color: theme.textSecondary }}
      >
        Pour chaque catégorie, ajoutez des lignes avec le nom, la répartition en % entre client et
        prestataire (total 100&nbsp;%), et le montant annuel HT.
      </p>

      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 md:gap-5">
        {FRAIS_GENERALES_CATEGORIES.map((cat) => {
          const lines = linesByCategory[cat.id] || [];
          const catTotal = categoryTotals[cat.id] || 0;

          return (
            <Card
              key={cat.id}
              className="flex min-w-0 flex-col overflow-hidden rounded-2xl border shadow-sm"
              style={{ borderColor: theme.border, backgroundColor: theme.surface }}
            >
            <CardHeader
              className="border-b space-y-1 px-4 py-4"
              style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
            >
              <p className="text-sm font-semibold sm:text-base" style={{ color: theme.text }}>
                {cat.label}
              </p>
              <p className="text-xs tabular-nums font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                Total catégorie : {fmtEur(catTotal)} HT
              </p>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 p-4 sm:p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-bold uppercase tracking-wide sm:text-sm" style={{ color: theme.textSecondary }}>
                  Lignes
                </span>
                {canEdit && (
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-10 min-h-[40px] gap-1.5 rounded-xl px-3 text-xs font-semibold sm:h-9 touch-manipulation"
                    onClick={() => addLine(cat.id)}
                  >
                    <Plus className="h-4 w-4 shrink-0" />
                    Ajouter une ligne
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                {lines.length === 0 && (
                  <p className="text-xs py-1" style={{ color: theme.textSecondary }}>
                    Aucune ligne. Utilisez « Ajouter une ligne ».
                  </p>
                )}
                {lines.map((line) => {
                  const ok = repartitionOk(line);
                  return (
                    <div
                      key={line.id}
                      className="grid grid-cols-1 gap-3 rounded-xl border-2 p-3 sm:grid-cols-12 sm:items-end sm:gap-3 sm:p-4"
                      style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
                    >
                      <div className="sm:col-span-4 space-y-1.5">
                        <Label className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                          Nom
                        </Label>
                        <Input
                          value={line.name}
                          onChange={(e) => updateLine(cat.id, line.id, { name: e.target.value })}
                          disabled={!canEdit}
                          placeholder="Désignation"
                          className={`${fieldH} text-sm`}
                          style={inputStyle}
                        />
                        {!ok && (
                          <span className="block text-[10px] leading-tight text-amber-600">
                            Client + Prestataire doit valoir 100&nbsp;%.
                          </span>
                        )}
                      </div>
                      <div className="sm:col-span-2 space-y-1.5">
                        <Label className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                          Client (%)
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          inputMode="decimal"
                          value={line.clientPct}
                          onChange={(e) => updateLine(cat.id, line.id, { clientPct: e.target.value })}
                          disabled={!canEdit}
                          className={`${fieldH} text-right text-sm`}
                          style={inputStyle}
                        />
                      </div>
                      <div className="sm:col-span-2 space-y-1.5">
                        <Label className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                          Prestataire (%)
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          inputMode="decimal"
                          value={line.prestatairePct}
                          onChange={(e) =>
                            updateLine(cat.id, line.id, { prestatairePct: e.target.value })
                          }
                          disabled={!canEdit}
                          className={`${fieldH} text-right text-sm`}
                          style={inputStyle}
                        />
                      </div>
                      <div className="sm:col-span-3 space-y-1.5">
                        <Label className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                          Montant (€ HT)
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          step={0.01}
                          inputMode="decimal"
                          value={line.amountHt === '' ? '' : line.amountHt}
                          onChange={(e) => updateLine(cat.id, line.id, { amountHt: e.target.value })}
                          disabled={!canEdit}
                          className={`${fieldH} text-right text-sm`}
                          style={inputStyle}
                        />
                      </div>
                      {canEdit && (
                        <div className="sm:col-span-1 flex justify-end pb-0.5 sm:pb-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-11 w-11 min-h-[44px] min-w-[44px] text-destructive hover:text-destructive touch-manipulation"
                            aria-label="Supprimer la ligne"
                            onClick={() => removeLine(cat.id, line.id)}
                          >
                            <Trash2 className="h-5 w-5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default DevisQuoteFraisGenerauxTab;
